import { type ReactNode, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { sanitizeFilename } from "@/lib/file-download";

const MAX_FILENAME_LENGTH = 200;

export interface ExportFilenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultFilename: string;
  extension: string;
  onExport: (filename: string) => void;
  children?: ReactNode;
  disabled?: boolean;
}

export function ExportFilenameDialog({
  open,
  onOpenChange,
  defaultFilename,
  extension,
  onExport,
  children,
  disabled = false,
}: ExportFilenameDialogProps) {
  const [filenameValue, setFilenameValue] = useState(defaultFilename);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset form when popover opens/closes or defaultFilename changes
  useEffect(() => {
    if (open) {
      // Strip extension from defaultFilename if present
      // Escape special regex characters in extension (using lodash-style pattern)
      const escapedExt = extension.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&");
      const withoutExt = defaultFilename.replace(
        new RegExp(`\\.${escapedExt}$`, "i"),
        "",
      );
      setFilenameValue(withoutExt);
      setError(null);

      // Select all text after a brief delay to ensure input is rendered
      const timeoutId = setTimeout(() => {
        inputRef.current?.select();
      }, 50);

      // Cleanup timeout on unmount or when dependencies change
      return () => clearTimeout(timeoutId);
    }
  }, [open, defaultFilename, extension]);

  const handleExport = () => {
    const trimmedFilename = filenameValue.trim();

    // Validation - check if empty before any processing
    if (!trimmedFilename) {
      setError("Filename cannot be empty");
      return;
    }

    if (trimmedFilename.length > MAX_FILENAME_LENGTH) {
      setError(`Filename too long (max ${MAX_FILENAME_LENGTH} characters)`);
      return;
    }

    // Strip any extension the user might have typed
    // Escape special regex characters in extension (using lodash-style pattern)
    const escapedExt = extension.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&");
    const withoutExtension = trimmedFilename.replace(
      new RegExp(`\\.${escapedExt}$`, "i"),
      "",
    );

    // Validate again after stripping extension (prevents ".csv" -> "" issue)
    const trimmedWithoutExt = withoutExtension.trim();
    if (!trimmedWithoutExt) {
      setError("Filename cannot be just an extension");
      return;
    }

    // Sanitize the filename
    const sanitized = sanitizeFilename(trimmedWithoutExt);

    // Close popover immediately (non-blocking)
    onOpenChange(false);

    // Call the export handler with sanitized filename (without extension)
    onExport(sanitized);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleInputChange = (value: string) => {
    setFilenameValue(value);
    // Clear validation error when user starts typing
    if (error && value.trim()) {
      setError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleExport();
    }
  };

  // If children provided, use Popover (anchored to trigger button)
  if (children) {
    return (
      <Popover open={open && !disabled} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <span>{children}</span>
        </PopoverTrigger>
        <PopoverContent className="w-[520px]" align="end" sideOffset={5}>
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Export File</h4>
            </div>

            <div className="grid gap-2">
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  id="filename"
                  placeholder="Enter filename..."
                  value={filenameValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => e.target.select()}
                  className={error ? "border-destructive flex-1" : "flex-1"}
                  autoFocus
                />
                <Badge variant="secondary" className="shrink-0">
                  .{extension}
                </Badge>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <p className="text-xs text-muted-foreground">
                Special characters will be replaced with underscores
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleExport}
                disabled={!filenameValue.trim()}
              >
                Export
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // No children - use lightweight Dialog for dropdown-triggered exports
  // Dialog closes immediately on Export, so user can continue working
  if (!open || disabled) return null;

  return (
    <Dialog open={open && !disabled} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] bg-card">
        <DialogHeader>
          <DialogTitle>Export File</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                id="filename-dialog"
                placeholder="Enter filename..."
                value={filenameValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={(e) => e.target.select()}
                className={error ? "border-destructive flex-1" : "flex-1"}
                autoFocus
              />
              <Badge variant="secondary" className="shrink-0">
                .{extension}
              </Badge>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <p className="text-xs text-muted-foreground">
              Special characters will be replaced with underscores
            </p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleExport}
              disabled={!filenameValue.trim()}
            >
              Export
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
